<template>
    <li
        ref="el"
        :data-productid="productId"
        :class="[
            'product-item',
            mobile ? 'forced-mobile' : '',
            isGalleryItem ? 'gallery-item' : '',
        ]"
        data-item-type="product"
        :data-page="page"
        :key="productId"
    >
        <div class="product-image-container">
            <component
                :is="link != null && link != '' ? 'a' : 'span'"
                :href="link"
                class="product-image"
                data-product-link
            >
                <div class="image-bounds">
                    <filler
                        v-if="imgInfo == null"
                        box
                        aspect-ratio="4/3"
                    ></filler>
                    <product-image
                        :scale-of-screen="scaleOfScreen"
                        :item="imgInfo"
                        :fixed-width="isGalleryItem ? undefined : undefined"
                        :provided-image-width="isGalleryItem ? 230 : undefined"
                        fill-width
                        :lazy="
                            forceLazyLoadImage ||
                            indexOfList == null ||
                            indexOfList > 4
                        "
                        fallback-width="700"
                        fallback-height="525"
                        fallback-aspect-ratio="4/3"
                        v-else
                    ></product-image>
                    <image-overlay
                        :type="overlayTextType"
                        :key="overlayTextType"
                        v-if="overlayText != null"
                        >{{ overlayText }}</image-overlay
                    >
                    <image-overlay
                        type="third-party-stamp"
                        key="third-party-stamp"
                        v-if="thirdPartyStamp"
                    ></image-overlay>
                    <image-overlay
                        type="related-auction"
                        key="related-auction"
                        v-if="relatedAuctionStamp"
                    ></image-overlay>
                    <image-overlay
                        type="related-shop"
                        key="related-shop"
                        v-if="relatedShopStamp"
                    ></image-overlay>
                    <div
                        class="discount-badge"
                        v-if="
                            isShop &&
                            productValue != null &&
                            price != null &&
                            productValue > price &&
                            Math.round((1 - price / productValue) * 100) >= 10
                        "
                    >
                        {{
                            format(
                                labels.frontendProductList.discount,
                                Math.round((1 - price / productValue) * 100) +
                                    "%"
                            )
                        }}
                    </div>
                    <follow-button
                        class="follow-action-floating"
                        v-if="mobile && isAuction && productId != null"
                        :product-id="productId"
                    ></follow-button>
                </div>
            </component>
        </div>
        <div class="product-details-container">
            <div
                v-if="
                    (productId == null && expectedContentType != 'shop') ||
                    (secondsLeft != null && isValidSecondsleft(secondsLeft))
                "
                class="time-container"
            >
                <span class="time-header">
                    <template v-if="productId == null">&nbsp;</template>
                    <template v-else>
                        {{ timeLabel }}
                    </template>
                </span>
                <div class="time-blocks">
                    <template v-if="secondsLeft != null">
                        <time-part
                            :forced-mobile="mobile"
                            :value="
                                secondsLeft == null
                                    ? undefined
                                    : secondsLeft > 0
                                    ? timeParts.days.value
                                    : 0
                            "
                            :unit="timeParts.days.unit"
                        ></time-part>
                        <span class="time-block-separator"></span>
                        <time-part
                            :forced-mobile="mobile"
                            :value="
                                secondsLeft == null
                                    ? undefined
                                    : secondsLeft > 0
                                    ? timeParts.hours.value
                                    : 0
                            "
                            :unit="timeParts.hours.unit"
                        ></time-part>
                        <span class="time-block-separator"></span>
                        <time-part
                            :forced-mobile="mobile"
                            :value="
                                secondsLeft == null
                                    ? undefined
                                    : secondsLeft > 0
                                    ? timeParts.minutes.value
                                    : 0
                            "
                            :unit="timeParts.minutes.unit"
                        ></time-part>
                        <span class="time-block-separator"></span>
                        <time-part
                            :forced-mobile="mobile"
                            :value="
                                secondsLeft == null
                                    ? undefined
                                    : secondsLeft > 0
                                    ? timeParts.seconds.value
                                    : 0
                            "
                            :unit="timeParts.seconds.unit"
                        ></time-part>
                    </template>
                </div>
            </div>

            <hr class="name-top-line" />

            <component
                :is="link != null && link != '' ? 'a' : 'span'"
                class="product-title"
                v-once
                :href="link"
                data-product-link
                ><template v-if="productId == null"
                    ><filler
                        v-if="productName == null"
                        :max="70"
                    ></filler></template
                ><template v-else>{{ productName }}</template></component
            >

            <hr class="name-bottom-line" />

            <div
                class="amounts auction-amounts"
                v-if="isAuction || productId == null"
            >
                <div
                    class="bid-amount"
                    v-if="price != null || productId == null"
                >
                    <template v-if="productId == null">&nbsp;</template>
                    <template v-else-if="!hasBid">
                        {{
                            labels.frontendProductList.productSpecs.headers
                                .minimumPrice
                        }}:
                    </template>
                    <template v-else>
                        {{
                            labels.frontendProductList.productSpecs.headers
                                .currentBid
                        }}:
                    </template>

                    <template v-if="productId == null || price == null"
                        >&nbsp;</template
                    >
                    <template v-else>
                        {{ formatAmount(price) }}
                    </template>
                </div>
                <div class="value-amount">
                    <template v-if="productValue == null || productId == null"
                        >&nbsp;</template
                    >
                    <template v-else>
                        {{
                            undefinedIfNullOrWhiteSpace(
                                overwriteSettings?.overwriteEstimateLabel
                            ) ??
                            labels.frontendProductList.productSpecs.headers
                                .estimatedValue
                        }}:
                    </template>

                    <template v-if="productId == null || productValue == null"
                        >&nbsp;</template
                    >
                    <template v-else>
                        {{ formatAmount(productValue) }}
                    </template>
                </div>
            </div>
            <div class="amounts" v-else-if="isShop">
                <div class="shop-amount" v-if="price != null">
                    {{ formatAmount(price) }}
                </div>
                <div
                    class="value-amount line-through"
                    v-if="
                        productValue != null &&
                        (price == null || productValue > price)
                    "
                >
                    <template
                        v-if="
                            productValue != null &&
                            (price == null || productValue > price)
                        "
                        >{{
                            undefinedIfNullOrWhiteSpace(
                                overwriteSettings?.overwriteEstimateLabel
                            ) ??
                            labels.frontendProductList.productSpecs.headers
                                .shopEstimatedPrice
                        }}: {{ formatAmount(productValue) }}</template
                    >
                    <template v-else>&nbsp;</template>
                </div>
            </div>

            <div
                v-if="
                    isAuction ||
                    isShop ||
                    isBuynow ||
                    overwriteActionButton ||
                    productId == null
                "
                :class="[
                    'actions-container',
                    isAuction ? 'auction-actions-container' : '',
                ]"
            >
                <div class="call-to-action">
                    <button
                        type="button"
                        :class="[
                            'action-button',
                            compActionButtonLoading ? 'loading' : '',
                        ]"
                        :disabled="compActionButtonLoading"
                        @click="$emit('action-button-click', $event)"
                        v-if="overwriteActionButton"
                    >
                        {{ overwriteActionButtonText }}
                    </button>
                    <component
                        :is="link != null && link != '' ? 'a' : 'span'"
                        :class="[
                            'action-button',
                            compActionButtonLoading ? 'loading' : '',
                        ]"
                        v-else
                        :href="link ?? (addToCartAction ? undefined : '#')"
                        data-product-link
                        @click="clickEvent($event)"
                    >
                        {{ actionButtonText }}
                    </component>
                    <fade-and-slide>
                        <div
                            :class="['success-text']"
                            v-if="
                                !isNullOrWhitespace(cartLink) &&
                                cartLink != null
                            "
                        >
                            <template
                                v-for="textPart in mapLabels(
                                    labels.frontendProductpage.specs.addedToCart.split(
                                        '{0}'
                                    )
                                )"
                                >{{ textPart.txt
                                }}<a
                                    v-if="textPart.i != textPart.a.length - 1"
                                    :href="cartLink"
                                    :class="['cart-link']"
                                    >{{
                                        labels.frontendProductpage.specs
                                            .addedToCartName
                                    }}</a
                                ></template
                            >
                        </div>
                    </fade-and-slide>
                </div>

                <div
                    class="follow-action"
                    v-if="!mobile && isAuction && productId != null"
                >
                    <follow-button :product-id="productId"></follow-button>
                </div>
            </div>
        </div>
        <slot></slot>
    </li>
</template>

<style scoped lang="scss">
@import "common.css.src/mixins.scss";

li + li {
    margin-top: 10px;
}

.success-text {
    text-align: center;
    text-transform: none;
    margin-top: 0.5em;
}
.cart-link {
    text-decoration: underline;
}

.discount-badge {
    position: absolute;
    top: 0;
    right: 0;
    background-color: #cccccc;
    padding: 5px 14px;
    text-transform: uppercase;
    font-weight: 600;
    font-size: 13px;
}

@mixin item-style($desktop) {
    overflow: hidden;

    display: flex;

    flex-direction: column;
    align-items: stretch;
    @if $desktop == true {
        flex-direction: row;
    }

    .amounts {
        display: flex;
        flex-direction: column-reverse;
        align-items: center;
        text-transform: uppercase;
        @if $desktop == true {
            flex-direction: column;
            align-items: baseline;
            align-items: flex-start;
        }
        justify-content: normal;
        transition: min-height 400ms ease-out;

        &.auction-amounts {
            @if $desktop == true {
                align-self: flex-start;
                width: 50%;
                height: 50px;
                display: inline-flex;
            }
        }

        .bid-amount,
        .shop-amount {
            font-weight: bold;
            font-size: 16px;
            @if $desktop == true {
                font-size: 20px;
            }
        }
        .value-amount {
            font-size: 11px;
            &.line-through {
                text-decoration: line-through;
            }
            @if $desktop == true {
                font-size: 14px;
            }
        }
    }

    .product-image-container {
        flex: 0;
        @if $desktop == true {
            max-width: 45%;
            flex: 1;
        }

        display: flex;
        a.product-image,
        span.product-image {
            display: inline-block;
            width: 100%;
        }
        .product-image {
            :global(.product-image-overlay-container) {
                position: relative;
                width: 100%;
                height: 100%;
                margin: 0;
                padding: 0;
                box-sizing: border-box;
                pointer-events: none;
            }

            .image-bounds {
                position: relative;
                font-size: 0;
            }
            picture {
                + {
                    .product-image-overlay-container {
                        position: absolute;
                        top: 0;
                        left: 0;
                        width: 100%;
                        height: 100%;
                        margin: 0;
                        padding: 0;
                        box-sizing: border-box;
                        pointer-events: none;
                    }
                }
            }

            // @import "common.css.src/_product-image-overlay.scss";
            // .product-image-overlay.experience-city {
            //     display: block;
            //     position: absolute;
            //     top: 8px;
            //     left: 8px;
            //     min-width: 37%;
            //     background-color: #00b6b5;
            //     color: #fff;
            //     font-weight: 700;
            //     font-size: 11pt;
            //     padding: 0;
            //     margin: 0;
            //     line-height: 2;
            //     font-family: "Open Sans Condensed", "Open Sans", sans-serif;
            //     text-transform: uppercase;
            //     text-align: center;
            //     padding-left: 5px;
            //     padding-right: 5px;
            //     box-sizing: border-box;
            // }
        }
    }

    .product-details-container {
        display: flex;
        flex-direction: column;
        justify-content: flex-start;
        flex: 1;
        margin-top: 10px;
        margin-bottom: 10px;
        @if $desktop == true {
            justify-content: center;
            margin-top: 15px;
            margin-bottom: 15px;
            margin-left: 25px;
            margin-right: 25px;
        }
        .time-container {
            display: flex;
            flex-direction: column;
            @if $desktop == true {
                flex-direction: row;
            }
            justify-content: center;
            align-items: center;
            .time-header {
                flex: 0;
                text-align: right;
                white-space: nowrap;
                font-size: 13px;
                margin-bottom: 10px;

                text-transform: uppercase;
                font-weight: 800;

                @if $desktop == true {
                    font-weight: bold;
                    font-size: 15px;
                    margin-bottom: 0;
                }
            }
            .time-blocks {
                display: flex;
                flex-direction: row;
                justify-content: space-evenly;
                align-items: stretch;
                flex: 0;
                min-width: 150px;
                min-height: 36px;
                @if $desktop == true {
                    min-width: 220px;
                }

                .time-block-separator {
                    background: #979797;
                    display: inline-block;
                    width: 1px;
                    height: auto;
                }
            }
        }
    }

    &:not(.list-end-item) {
        border: 1px solid #d7d7d7;
        border-radius: 5px;
        /*height: 540px;*/
        @if $desktop == true {
            position: relative;
        }
    }

    &.list-end-item {
        display: block;
        flex-grow: 1;
        flex-basis: 100%;
    }

    box-sizing: content-box;

    @at-root .async-product-list #{&}.load-placeholder {
        opacity: 1;
        position: relative;
        height: 540px;

        @if $desktop == true {
            height: unset;
        }

        :before {
            display: block;
            position: absolute;
            top: 0px;
            left: 0px;
            bottom: 0px;
            right: 0px;
            background-color: rgba(221, 222, 224, 0.25);
            content: " ";
            visibility: visible;
            height: 100%;
            background-image: url("../../../../../../../mthemes/odendo.dk/images/ajax-loader.gif");
            background-repeat: no-repeat;
            background-position: center;
            width: 100%;
        }
    }

    .product-title {
        display: block;
        margin-right: 3px;
        margin-left: 3px;
        font-size: 12px;
        height: 50px;
        overflow: hidden;
        @if $desktop == true {
            font-size: 18px;
            margin: 0;
        }
        text-align: center;
        transition: min-height 400ms ease-out;

        text-overflow: ellipsis;
        display: -webkit-box;
        -webkit-line-clamp: 3;
        -webkit-box-orient: vertical;
    }

    .name-top-line,
    .name-bottom-line {
        margin-top: 12px;
        margin-bottom: 12px;
        margin-left: 15px;
        margin-right: 15px;
        @if $desktop == true {
            margin-left: 0px;
            margin-right: 0px;
        }
        width: auto;
        max-height: 1px;
        border: none;
        border-top: 1px solid #979797;
    }

    + li {
        margin-top: 10px;
    }

    &.advert-item {
        height: auto;

        @if $desktop == true {
            height: auto !important;
        }

        img.full-width {
            width: 100%;
            height: auto;
        }
    }

    .list-action-popup {
        @if $desktop == true {
            visibility: hidden;
            opacity: 0;
            z-index: 1;
            transition: opacity 400ms, visibility 0s;
            transition-delay: 0s, 400ms;
            position: absolute;
            top: 67px;
            border: 1px solid black;
            background-color: #fff;
            width: 300px;

            &[data-sender-action="open-popup-buynow"] {
                top: 186px;
            }

            &.visible {
                visibility: visible;
                opacity: 1;
                transition-delay: 0s, 0s;
            }
        }
    }

    .estimated-price-container {
        text-align: center;
        text-transform: uppercase;
        > span {
            color: #666;
            font-weight: 600;
            font-size: 12px;
        }
        &:not(.no-line-through) {
            > span {
                /*text-decoration: line-through;*/
                text-decoration: underline;
                color: #272727;
                font-family: "Open Sans";
                font-size: 12px;
                font-weight: normal;
                text-transform: none;
                line-height: 20px;
            }
        }
    }

    li {
        .auction-and-buynow {
            &:not(.price-comparison-enabled) {
                .price {
                    font-size: 17px !important;
                    margin-left: 15px;
                    margin-right: 15px;
                }

                .header-text {
                    &:not(.time) {
                        font-size: 13px !important;
                    }
                }

                .action-button {
                    @if $desktop == true {
                        font-size: 15px !important;
                    }
                }
            }
        }
    }

    .spec.row.shop-product-stock-status {
        padding: 0 !important;
        margin-top: 10px;
        margin-bottom: 10px;
    }

    .actions-container {
        display: flex;
        flex-direction: row;
        justify-content: center;
        margin-top: 10px;
        text-transform: uppercase;

        .call-to-action {
            text-align: center;
        }

        &.auction-actions-container {
            @if $desktop == true {
                margin-top: -45px;
                place-self: flex-end;
                display: inline-flex;
            }
        }
    }

    .follow-action {
        margin-left: 10px;
    }

    .follow-action-floating {
        position: absolute;
        right: 8px;
        top: 8px;
    }

    .action-button {
        background-color: #00b6b5;
        color: #000;
        font-size: 14px;
        box-sizing: border-box;
        border: 2px solid #02b6b5;
        border-radius: 4em;
        font-weight: 700;
        text-align: center;
        position: relative;
        cursor: pointer;
        display: inline-block;
        width: auto;
        padding: 12px 32px;
        min-width: 120px;
        padding: 10px 32px;

        @if $desktop == true {
            min-width: 150px;
            padding: 10px 5px;
        }

        &.loading {
            &:before {
                background-image: url("../../../../../../../mthemes/odendo.dk/images/ajax-loader.gif") !important;
                background-repeat: no-repeat;
                background-size: 25px !important;
                background-position: center;
                background-color: inherit;
                opacity: 0.8;
                border-radius: inherit;
                content: "";
                display: block;
                width: 100%;
                height: 100%;
                position: absolute;
                top: 0px;
                left: 0px;
                right: 0px;
                bottom: 0px;
                cursor: inherit;
                pointer-events: none;
            }
        }
    }

    @at-root [data-list-guid="129"] #{&} {
        .product-title {
            font-size: 17px;
            font-weight: 700;
            order: -4;
        }
        .name-top-line {
            order: -3;
        }
        .amounts {
            order: -2;

            .bid-amount,
            .shop-amount {
                font-size: 20px;

                font-weight: bold;
            }
        }
        .name-bottom-line {
            order: -1;
        }

        .product-details-container {
            .time-container {
                .time-header {
                    text-transform: none;
                    font-weight: 700;
                }
            }
        }
    }
}

li.product-item {
    @include item-style(false);
    &:not(.forced-mobile) {
        @include when-desktop() {
            @include item-style(true);
        }
    }

    .product-image-container {
        .product-image {
            @import "common.css.src/_product-image-overlay.scss";
        }
    }
}
</style>

<script lang="ts">
export default {
    model: {
        prop: "item",
    },
};
</script>

<script setup lang="ts">
import {
    getDateParts,
    isValidSecondsleft,
} from "../../../../../commons/product/auction/countdown";
import { formatAmount } from "../common";
import labels from "../../../../shared/labels";
import ImageOverlay from "../../../../../commons/product/overlays/image-overlay.vue";
import {
    type TrackingInformation,
    storeLinkTrackingInformation,
} from "../../../../../../utilities/product-link-tracking";
import {
    type PropType,
    ref,
    onMounted,
    watch,
    computed,
    onBeforeUnmount,
} from "vue";
import productImage from "../common/product-image.vue";
import timePart from "./time-part.vue";
import { type IProductPictureInfo } from "../../list-item";
import { TimeDuration } from "@and3k5/timespan.js";
import {
    format,
    isNullOrEmpty,
    isNullOrWhitespace,
    undefinedIfNullOrWhiteSpace,
} from "../../../../../../shared/utils/string";
import fallbackImage from "../../../../frontend-productpage/fallback-image.jpg?file";
import FollowButton from "../common/follow-button.vue";
import { type TimeParts } from "../../../../../commons/product/auction/countdown/types";
import FadeAndSlide from "../../../../shared/FadeAndSlide.vue";
import axios from "axios";
import {
    type AddToCartResponse,
    afterAddToCart,
} from "../../../../frontend-productpage/shop/cart-adding";
import { type OverwriteSettings } from "../../../../../commons/product/overwrite-settings";
import Filler from "../../../../shared/Filler.vue";
import {
    ensureInitializedRegistration,
    type ProductRegistrationResult,
} from "../common/product-updater";

const emit = defineEmits([
    "action-button-click",
    "update:productId",
    "update:productName",
    "update:link",
    "update:overlayText",
    "update:overlayTextType",
    "update:thirdPartyStamp",
    "update:relatedAuctionStamp",
    "update:relatedShopStamp",
    "update:secondsLeft",
    "update:timeIsNotStarted",
    "update:isAuction",
    "update:isShop",
    "update:isBuynow",
    "update:productValue",
    "update:hasBid",
    "update:price",
    "mounted",
    "unmount",
]);

const cartLink = ref<string>();

const props = defineProps({
    isGalleryItem: Boolean,
    productId: Number,
    productName: String,
    link: String,
    overlayText: String,
    overlayTextType: String,
    thirdPartyStamp: Boolean,
    relatedAuctionStamp: Boolean,
    relatedShopStamp: Boolean,
    mobile: Boolean,
    forceLazyLoadImage: Boolean,
    secondsLeft: Number,
    indexOfList: Number,
    timeIsNotStarted: Boolean,
    imageInfo: Object as PropType<IProductPictureInfo>,
    page: Number,
    isAuction: Boolean,
    isShop: Boolean,
    isBuynow: Boolean,
    trackingInfo: Object as PropType<TrackingInformation>,
    productValue: Number,
    hasBid: Boolean,
    price: Number,
    scaleOfScreen: {
        type: Number,
        default: 1,
    },
    overwriteActionButtonText: String,
    overwriteActionButton: Boolean,
    overwriteSettings: Object as PropType<OverwriteSettings>,
    actionButtonLoading: Boolean,
    expectedContentType: String,
    visible: {
        type: Boolean,
        default: true,
    },
    registration: Object as PropType<ProductRegistrationResult>,
});

const timeLabel = computed(() => {
    if (props.isShop) {
        if (props.timeIsNotStarted)
            return labels.frontendProductList.productSpecs.headers
                .timeLeftToStart_shop;
        else
            return labels.frontendProductList.productSpecs.headers
                .timeLeft_shop;
    }
    if (props.timeIsNotStarted)
        return labels.frontendProductList.productSpecs.headers.timeLeftToStart;
    else return labels.frontendProductList.productSpecs.headers.timeLeft;
});

function mapLabels(value: string[]) {
    return value.map((x: string, i: number, a: string[]) => ({
        i: i,
        txt: x,
        a: a,
    }));
}

const duration = new TimeDuration(0);
const timeParts = ref<TimeParts>({
    days: {
        unit: "\u00a0",
        value: undefined,
    },
    hours: {
        unit: "\u00a0",
        value: undefined,
    },
    minutes: {
        unit: "\u00a0",
        value: undefined,
    },
    seconds: {
        unit: "\u00a0",
        value: undefined,
    },
});
const internalActionButtonLoading = ref(false);

const imgInfo = computed(() => {
    if (props.imageInfo == null) return props.imageInfo;
    if (props.imageInfo.pictureUrl != null) return props.imageInfo;
    const res: IProductPictureInfo = {
        pictureUrl: fallbackImage,
        pictureWidth: 470,
        pictureHeight: 353,
        pictureSrcSet: undefined,
        pictureSrcSetAsWebp: undefined,
        pictureUrlAsWebp: undefined,
    };
    return res;
});

watch(
    () => props.secondsLeft,
    (newValue, oldValue) => {
        if (props.visible != true) return;
        if (props.productId == null) return;
        if (newValue != null) {
            if (oldValue != null && ~~newValue == ~~oldValue) return;
            updateSecondsData(newValue);
        }
    },
    {
        immediate: true,
    }
);

watch(
    () => props.visible,
    (newValue, oldValue) => {
        if (newValue && !oldValue) {
            if (props.productId == null) {
                // console.debug("would get seconds but productid missing");
                return;
            }
            if (props.secondsLeft == null) {
                // console.debug("would get seconds but secondsLeft missing");
                return;
            }
            updateSecondsData(props.secondsLeft);
        } else {
            // console.log(`visible ${oldValue} => ${newValue}`);
        }
    }
);

function updateSecondsData(newValue: number) {
    duration.setTotalSeconds(newValue);
    timeParts.value = getDateParts(
        duration,
        labels.frontendProductList.productSpecs.time
    );
}

const addToCartAction = computed(() => {
    if (props.isShop !== true) {
        return false;
    }
    return true;
});

const compActionButtonLoading = computed(
    () => internalActionButtonLoading.value || props.actionButtonLoading
);

const actionButtonText = computed(() => {
    if (props.productId == null) {
        return "\u00a0";
    }
    if (addToCartAction.value) {
        return labels.frontendProductpage.specs.addToCart;
    }
    if (
        props.isShop &&
        !isNullOrEmpty(
            labels.frontendProductList.productSpecs.actionTexts.makeOffer_shop
        )
    )
        return labels.frontendProductList.productSpecs.actionTexts
            .makeOffer_shop;
    else if (
        props.isBuynow &&
        !isNullOrEmpty(
            labels.frontendProductList.productSpecs.actionTexts.makeOffer_buyNow
        )
    )
        return labels.frontendProductList.productSpecs.actionTexts
            .makeOffer_buyNow;
    else return labels.frontendProductList.productSpecs.actionTexts.makerOffer;
});

async function clickEvent(ev: Event) {
    if (props.productId == null) {
        ev.preventDefault();
        return;
    }
    if (addToCartAction.value) {
        ev.preventDefault();
        internalActionButtonLoading.value = true;
        try {
            const response = await axios.post<AddToCartResponse>(
                "/auctionProduct/addToCart",
                undefined,
                {
                    params: {
                        productId: props.productId,
                        orderQuantity: 1,
                    },
                }
            );
            if (response.data.success !== true) {
                throw new Error(response.data.errors.join("\n"));
            }

            afterAddToCart(
                props.productId,
                response.data.cartItemsCount,
                response.data.firstAdd
            );

            cartLink.value = response.data.cartLink;

            return;
        } finally {
            internalActionButtonLoading.value = false;
        }
    }
}

const el = ref<HTMLLIElement>();

onMounted(() => {
    emit("mounted", el.value);
    ensureInitializedRegistration(props, emit);

    if (el.value == null) throw new Error("missing ref");
    if (props.trackingInfo != null) {
        // console.debug("storeLinkTrackingInformation");
        storeLinkTrackingInformation(el.value, props.trackingInfo);
    }
});

onBeforeUnmount(() => {
    emit("unmount", el.value);
});
</script>
